import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import { Label } from 'src/components/ui/label';
import { Calendar as CalendarIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { useToast } from 'src/hooks/use-toast';
import { usePartnerList } from 'src/services/api/queries/partnerQueries';
import {
  useAssignmentInstitutions,
  useJobInstitutions,
} from 'src/services/api/queries/opportunityQueries';
import {
  useAssignInstitutionToAssignment,
  useAssignInstitutionToJob,
} from 'src/services/api/mutations/opportunityMutations';
import { Calendar } from 'src/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';

const parseApiDate = (dateString) => {
  if (!dateString) return null;
  if (dateString.includes('/')) {
    const [dd, mm, yyyy] = dateString.split('/');
    if (dd && mm && yyyy) {
      const parsedDate = new Date(`${yyyy}-${mm}-${dd}`);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
  }
  const parsedDate = new Date(dateString);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const EngageInstitutionModal = ({
  isOpen,
  onClose,
  opportunityId,
  assignmentId,
  jobCode,
}) => {
  const { toast } = useToast();
  const [selectedInstitutionId, setSelectedInstitutionId] = useState('');
  const [deadline, setDeadline] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isTemplatePrefilled, setIsTemplatePrefilled] = useState(false);
  const { data, isLoading, error } = usePartnerList({
    enabled: isOpen,
    page: 1,
    pageSize: 100,
    partnerType: 2,
  });
  const isJobPosting = !!jobCode;
  const {
    data: assignmentInstitutionsData,
    error: assignmentInstitutionsError,
    isLoading: isAssignmentInstitutionsLoading,
  } = useAssignmentInstitutions(assignmentId, isOpen && !isJobPosting);
  const {
    data: jobInstitutionsData,
    error: jobInstitutionsError,
    isLoading: isJobInstitutionsLoading,
  } = useJobInstitutions(jobCode, isOpen && isJobPosting);
  const assignInstitutionToAssignmentMutation =
    useAssignInstitutionToAssignment();
  const assignInstitutionToJobMutation = useAssignInstitutionToJob();
  const assignInstitutionMutation = isJobPosting
    ? assignInstitutionToJobMutation
    : assignInstitutionToAssignmentMutation;

  const institutions = useMemo(
    () => data?.partners || data?.data?.partners || data?.items || [],
    [data]
  );
  const assignmentData = useMemo(() => {
    if (isJobPosting) {
      return jobInstitutionsData?.data || jobInstitutionsData?.data?.data || {};
    }
    return (
      assignmentInstitutionsData?.data ||
      assignmentInstitutionsData?.data?.data ||
      {}
    );
  }, [assignmentInstitutionsData, jobInstitutionsData, isJobPosting]);
  const institutionAssignments = useMemo(
    () => assignmentData?.institution_assignments || [],
    [assignmentData]
  );
  const engagedInstitutionOrgIds = useMemo(
    () =>
      institutionAssignments
        .map((item) => item?.partner?.partner_org_id)
        .filter(Boolean)
        .map((id) => id.toString()),
    [institutionAssignments]
  );
  const assignmentDeadline = assignmentData?.deadline || '';
  const templateSubject = assignmentData?.template_data?.job_subject_line || '';
  const templateBody = assignmentData?.template_data?.job_email_body || '';

  useEffect(() => {
    if (!isOpen) {
      setSelectedInstitutionId('');
      setDeadline(null);
      setEmailSubject('');
      setEmailBody('');
      setIsTemplatePrefilled(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setDeadline(parseApiDate(assignmentDeadline));
  }, [isOpen, assignmentDeadline]);

  useEffect(() => {
    if (!isOpen || isTemplatePrefilled) return;
    if (templateSubject || templateBody) {
      setEmailSubject(templateSubject);
      setEmailBody(templateBody);
      setIsTemplatePrefilled(true);
    }
  }, [isOpen, isTemplatePrefilled, templateSubject, templateBody]);

  useEffect(() => {
    if (error) {
      toast({
        message: error.message || 'Failed to fetch institutions.',
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  useEffect(() => {
    const institutionError = isJobPosting
      ? jobInstitutionsError
      : assignmentInstitutionsError;
    if (institutionError) {
      toast({
        message:
          institutionError.message ||
          `Failed to fetch ${isJobPosting ? 'job' : 'assignment'} institutions.`,
        variant: 'destructive',
      });
    }
  }, [assignmentInstitutionsError, jobInstitutionsError, isJobPosting, toast]);

  useEffect(() => {
    if (!selectedInstitutionId) return;
    if (engagedInstitutionOrgIds.includes(selectedInstitutionId)) {
      setSelectedInstitutionId('');
    }
  }, [engagedInstitutionOrgIds, selectedInstitutionId]);

  const handleSend = async () => {
    const identifier = isJobPosting ? jobCode : assignmentId;
    if (!identifier) return;
    if (
      !selectedInstitutionId ||
      !emailSubject.trim() ||
      !emailBody.trim() ||
      !deadline
    ) {
      toast({
        message:
          'Please select institution, deadline, email subject, and email content.',
        variant: 'destructive',
      });
      return;
    }
    if (engagedInstitutionOrgIds.includes(selectedInstitutionId)) {
      toast({
        message: `Selected institution is already engaged for this ${
          isJobPosting ? 'job posting' : 'assignment'
        }.`,
        variant: 'destructive',
      });
      return;
    }

    const selectedInstitution = institutions.find((institution) => {
      const institutionValue = (
        institution?.org_id ??
        institution?.id ??
        ''
      ).toString();
      return institutionValue === selectedInstitutionId;
    });

    const payload = {
      partner_assignments: [
        {
          partner_org_id:
            selectedInstitution?.org_id || selectedInstitutionId || '',
          email_subject: emailSubject.trim(),
          email_content: emailBody.trim(),
          deadline: format(deadline, 'dd/MM/yyyy'),
        },
      ],
    };

    try {
      const response = await assignInstitutionMutation.mutateAsync(
        isJobPosting
          ? { jobCode, data: payload }
          : { assignmentId, data: payload }
      );
      if (response?.success === false) {
        throw new Error(response?.message || 'Failed to engage institution.');
      }
      toast({
        message: 'Institution engaged and email sent successfully.',
        variant: 'success',
      });
      onClose();
    } catch (submitError) {
      toast({
        message: submitError?.message || 'Failed to engage institution.',
        variant: 'destructive',
      });
    }
  };

  let engagedInstitutionsContent = (
    <div className="space-y-2">
      {institutionAssignments.map((item) => (
        <div
          key={item?.id || item?.partner?.partner_org_id}
          className="rounded border border-border bg-card px-3 py-2"
        >
          <p className="text-sm font-medium text-foreground-strong">
            {item?.partner?.name || 'Institution'}
          </p>
          <p className="text-xs text-muted-foreground">
            Engaged by {item?.assigned_by?.name || 'Unknown'}
          </p>
        </div>
      ))}
    </div>
  );

  const isLoadingInstitutions = isJobPosting
    ? isJobInstitutionsLoading
    : isAssignmentInstitutionsLoading;
  if (isLoadingInstitutions) {
    engagedInstitutionsContent = (
      <p className="text-sm text-muted-foreground">
        Loading engaged institutions...
      </p>
    );
  } else if (institutionAssignments.length === 0) {
    engagedInstitutionsContent = (
      <p className="text-sm text-muted-foreground">
        No institution engaged yet.
      </p>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Engage Institution</DialogTitle>
          <DialogDescription>
            Select institution and draft email details.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="grid gap-4">
          <div className="rounded-md border border-border bg-muted p-3">
            <p className="mb-2 text-xs font-semibold text-foreground uppercase tracking-wide">
              Already Engaged Institutions
            </p>
            {engagedInstitutionsContent}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="institution-select">Institution</Label>
            <Select
              value={selectedInstitutionId}
              onValueChange={setSelectedInstitutionId}
              disabled={isLoading || institutions.length === 0}
            >
              <SelectTrigger id="institution-select">
                <SelectValue
                  placeholder={
                    isLoading ? 'Loading institutions...' : 'Select institution'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {institutions.map((institution) => {
                  const institutionId = (
                    institution?.org_id ??
                    institution?.id ??
                    ''
                  ).toString();
                  const institutionName =
                    institution?.name ||
                    institution?.organization_name ||
                    institution?.org_name ||
                    'Institution';
                  if (!institutionId) return null;
                  const isAlreadyEngaged =
                    engagedInstitutionOrgIds.includes(institutionId);
                  return (
                    <SelectItem
                      key={institutionId}
                      value={institutionId}
                      disabled={isAlreadyEngaged}
                    >
                      {institutionName}
                      {isAlreadyEngaged ? ' (Already engaged)' : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="email-subject">Email Subject</Label>
            <Input
              id="email-subject"
              value={emailSubject}
              onChange={(event) => setEmailSubject(event.target.value)}
              placeholder="Enter email subject"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="engagement-deadline">Deadline</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="engagement-deadline"
                  variant="outline"
                  className="w-full justify-start text-left font-normal shadow-none"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {deadline ? (
                    format(deadline, 'PPP')
                  ) : (
                    <span>Select date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={deadline}
                  onSelect={setDeadline}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="email-body">Email Body / Content</Label>
            <Textarea
              id="email-body"
              value={emailBody}
              onChange={(event) => setEmailBody(event.target.value)}
              rows={6}
              placeholder="Enter email content"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {opportunityId && `Opportunity ID: ${opportunityId}`}
            {assignmentId && ` | Assignment ID: ${assignmentId}`}
            {jobCode && ` | Job Code: ${jobCode}`}
            {!opportunityId && !assignmentId && !jobCode && 'N/A'}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              assignInstitutionMutation.isPending ||
              !(isJobPosting ? jobCode : assignmentId) ||
              !selectedInstitutionId ||
              !deadline ||
              !emailSubject.trim() ||
              !emailBody.trim()
            }
          >
            {assignInstitutionMutation.isPending ? 'Sending...' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EngageInstitutionModal;
